<template>
    <div class="row">
        <!-- Campo de seleção -->
        <div class="form-item">
            <label class="label-caption">{{ label }}</label>
            <input
                ref="fileInput"
                type="file"
                :accept="accept"
                @change="onFileChange"
                :disabled="disabled || isUploading"
            />
        </div>

        <!-- Card lateral: tamanho + limpar -->
        <div v-if="fileName">
            <div class="file-card">
                <span class="file-size">{{ fileSize }}</span>

                <button
                    type="button"
                    class="btn-clear"
                    @click="clear"
                    :disabled="isUploading"
                    title="Limpar arquivo"
                >
                    <img src="@/assets/icons/eraser.svg" alt="Limpar" />
                </button>
            </div>
        </div>
    </div>

    <div v-if="localError" class="error-message">{{ localError }}</div>
</template>


<script setup>
import { computed, ref } from 'vue'
import { useAnexoUpload } from '@/composables/useAnexoUpload'

const emit = defineEmits(['selected', 'cleared'])

const props = defineProps({
    label: { type: String, default: 'Selecionar Arquivo' },
    accept: { type: String, default: '.pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.csv,.txt,.odt,.ods' },
    maxSizeMB: { type: Number, default: 20 },
    disabled: { type: Boolean, default: false },
})

const fileInput = ref(null)
const file = ref(null)
const localError = ref(null)

const { isUploading, uploadAnexo } = useAnexoUpload()

const fileName = computed(() => file.value?.name || '')
const fileSize = computed(() => {
    if (!file.value) return ''
    const mb = file.value.size / 1024 / 1024
    return `${mb.toFixed(2)} MB`
})

function validateSelectedFile() {
    localError.value = null
    if (!file.value) return { ok: false, message: 'Nenhum arquivo selecionado.' }

    const maxBytes = props.maxSizeMB * 1024 * 1024
    if (file.value.size > maxBytes) {
        return { ok: false, message: `Arquivo muito grande. Máximo: ${props.maxSizeMB} MB` }
    }
    return { ok: true }
}

function onFileChange(e) {
    localError.value = null
    const files = e.target?.files
    file.value = (files && files.length > 0) ? files[0] : null

    if (file.value) emit('selected', file.value)
    else emit('cleared')
}

function hasFile() {
    return !!file.value
}

function getFile() {
    return file.value
}

function clear() {
    file.value = null
    localError.value = null
    if (fileInput.value) fileInput.value.value = ''
    emit('cleared')
}

async function upload(docAnexo) {
    const v = validateSelectedFile()
    if (!v.ok) {
        localError.value = v.message
        throw new Error(v.message)
    }
    return await uploadAnexo({ docAnexo, file: file.value })
}

defineExpose({
    isUploading,
    hasFile,
    getFile,
    clear,
    validateSelectedFile,
    upload,
})
</script>

<style scoped>
.file-card{
    height: 28px;                             /* alinha com input */
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 2px;

    padding: 0 10px;
    
    border: 1px solid #a6a6a6;
    border-radius: 4px;

    background: #fafafa;
    max-width: 92px;
}

.file-size{
    font-size: 13px;
    color: #444;
    white-space: nowrap;
}

.btn-clear{
    width: 22px;
    min-width: 22px;
    height: 22px;

    padding: 0;
    margin: 0;

    border: none;
    background: none;

    cursor: pointer;

    display: flex;
    align-items: center;
    justify-content: center;

    flex: 0 0 auto;
}

.btn-clear img{
    width: 22px;
    height: 22px;
    opacity: .8;
}

.btn-clear:hover img{
    opacity: 1;
}

.btn-clear:disabled{
    cursor: not-allowed;
}

.btn-clear:disabled img{
    opacity: .4;
}

.error-message{
    margin-top: 6px;
    color: #b00020;
    font-size: 13px;
}
</style>
